import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, Instagram, Twitter, Youtube, Globe, Video, Trophy, ArrowLeft, Smartphone } from "lucide-react";
import { http, downloadUrl, embedUrl, FALLBACK_PHOTO, API, absoluteUrl } from "../lib/api";
import { Navbar, Footer } from "../components/Brand";
import { useAuth } from "../context/AuthContext";

const MARK = "top-right";
const THEME = "navy";

const SOCIAL_ICONS = {
  instagram: Instagram,
  twitter: Twitter,
  youtube: Youtube,
  tiktok: Video,
  hudl: Video,
  website: Globe,
};

const COUNTED_VIEWS = new Set();

export default function AthleteProfile() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [athlete, setAthlete] = useState(null);
  const viewedRef = useRef(null);

  const [error, setError] = useState(false);

  useEffect(() => {
    http
      .get(`/athletes/${slug}`)
      .then(({ data }) => {
        setAthlete(data);
        document.title = `${data.name} | ${data.position || data.sport} | Faith Up Athletes`;
        const desc = (data.bio || "").slice(0, 155) ||
          `${data.name} — ${data.position || data.sport} at ${data.team || "Faith Up Athletes"}.`;
        let m = document.querySelector('meta[name="description"]');
        if (!m) {
          m = document.createElement("meta");
          m.name = "description";
          document.head.appendChild(m);
        }
        m.content = desc;
      })
      .catch(() => setError(true));
    if (viewedRef.current !== slug && !COUNTED_VIEWS.has(slug)) {
      viewedRef.current = slug;
      COUNTED_VIEWS.add(slug);
      http.post(`/athletes/${slug}/view`).catch(() => {});
    }
  }, [slug]);

  if (error)
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="max-w-2xl mx-auto px-5 py-32 text-center" data-testid="athlete-not-found">
          <h1 className="font-display text-5xl">Athlete not found</h1>
          <Link to="/" className="label-mono text-[#FF5A00] mt-6 inline-block">
            ← Back to roster
          </Link>
        </div>
      </div>
    );

  if (!athlete)
    return (
      <div className="min-h-screen">
        <Navbar />
        <p className="label-mono p-10">Loading profile…</p>
      </div>
    );

  const hero = athlete.hero_url || athlete.photo_url || FALLBACK_PHOTO;
  const measurables = [
    ["Height", athlete.height],
    ["Weight", athlete.weight || "—"],
    ["Age", athlete.age],
  ].filter(([, v]) => v);

  return (
    <div className="min-h-screen" data-testid="athlete-profile-page">
      <Navbar />

      <section className="relative min-h-[70vh] flex items-end overflow-hidden">
        <img
          src={absoluteUrl(hero)}
          alt={athlete.name}
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            objectPosition: `${(athlete.hero_focus_x ?? 0.5) * 100}% ${(athlete.hero_focus_y ?? 0.35) * 100}%`,
            transform: `scale(${athlete.hero_zoom ?? 1})`,
            transformOrigin: `${(athlete.hero_focus_x ?? 0.5) * 100}% ${(athlete.hero_focus_y ?? 0.35) * 100}%`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/70 to-[#061224]/50" />
        <div className="relative max-w-[1400px] w-full mx-auto px-5 sm:px-10 py-14">
          <Link to="/" className="label-mono flex items-center gap-2 hover:text-[#FF5A00] mb-6">
            <ArrowLeft size={14} /> Roster
          </Link>
          <p className="label-mono text-[#FF5A00]">
            {athlete.jersey ? `#${String(athlete.jersey).replace("#", "")} · ` : ""}
            {[athlete.position, athlete.sport, athlete.team].filter(Boolean).join(" · ")}
          </p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-display text-6xl sm:text-7xl lg:text-8xl leading-[0.88] mt-2"
            data-testid="athlete-name"
          >
            {athlete.name}
          </motion.h1>

          <div className="flex flex-wrap gap-8 mt-8">
            {measurables.map(([label, val]) => (
              <div key={label}>
                <p className="label-mono">{label}</p>
                <p className="font-display text-4xl sm:text-5xl text-white">{val}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-3 mt-10">
            {user?.is_admin && athlete.photo_url && (
              <a
                data-testid="download-story-btn"
                href={`${API}/athletes/${athlete.slug}/story?position=${MARK}&theme=${THEME}`}
                className="clip-slant bg-[#FF5A00] hover:bg-[#FF7B33] transition-colors px-7 py-4 text-xs font-bold uppercase tracking-[0.18em] flex items-center gap-2"
              >
                <Smartphone size={16} /> Story 9:16
              </a>
            )}
          </div>

          <p className="label-mono mt-4">{user?.is_admin ? "Downloads are auto-stamped with the Faith Up logo" : ""}</p>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 sm:px-10 py-16 grid md:grid-cols-12 gap-8">
        <div className="md:col-span-7 space-y-8">
          {athlete.bio && (
            <div className="border border-white/10 bg-[#111111] p-8" data-testid="athlete-bio">
              <p className="label-mono text-[#FF5A00]">Scouting Report</p>
              <h2 className="font-display text-4xl mt-1 mb-4">The Story</h2>
              <p className="text-sm sm:text-base text-zinc-300 leading-relaxed whitespace-pre-line">
                {athlete.bio}
              </p>
            </div>
          )}

          {athlete.videos?.length > 0 && (
            <div className="space-y-4" data-testid="athlete-videos">
              <h2 className="font-display text-4xl">Highlight Film</h2>
              {athlete.videos.map((v, i) => {
                const embed = embedUrl(v.url);
                return (
                  <div key={i} className="border border-white/10 bg-[#111111]">
                    <div className="aspect-video bg-black">
                      {embed ? (
                        <iframe
                          src={embed}
                          title={v.title || `Highlight ${i + 1}`}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture"
                          allowFullScreen
                          className="w-full h-full"
                        />
                      ) : (
                        <video src={absoluteUrl(v.url)} controls className="w-full h-full" />
                      )}
                    </div>
                    <div className="flex items-center justify-between gap-4 p-4 border-t border-white/10">
                      <p className="font-display text-2xl">{v.title || `Highlight ${i + 1}`}</p>
                      <a
                        data-testid={`download-video-${i}`}
                        href={downloadUrl(v.url, `${athlete.slug}-highlight-${i + 1}`)}
                        className="label-mono flex items-center gap-2 text-[#FF5A00] hover:text-white"
                      >
                        <Download size={14} /> Download
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="md:col-span-5 space-y-8">
          {athlete.stats?.length > 0 && (
            <div className="border border-white/10 bg-[#061224] p-8" data-testid="athlete-stats">
              <p className="label-mono text-[#FF5A00]">Key Stats</p>
              <div className="grid grid-cols-2 gap-6 mt-5">
                {athlete.stats.map((s, i) => (
                  <div key={i}>
                    <p className="font-display text-5xl leading-none">{s.value}</p>
                    <p className="label-mono mt-1">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {athlete.achievements?.length > 0 && (
            <div className="border border-white/10 bg-[#111111] p-8" data-testid="athlete-achievements">
              <h2 className="font-display text-3xl mb-4">Achievements</h2>
              <ul className="space-y-3">
                {athlete.achievements.map((a, i) => (
                  <li key={i} className="flex gap-3 text-sm text-zinc-300">
                    <Trophy size={16} className="text-[#FF5A00] shrink-0 mt-0.5" />
                    {a}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {athlete.hometown && (
            <div className="border border-white/10 bg-[#111111] p-8">
              <p className="label-mono">Hometown</p>
              <p className="font-display text-3xl mt-1">{athlete.hometown}</p>
            </div>
          )}

          {Object.entries(athlete.socials || {}).some(([, v]) => v) && (
            <div className="border border-white/10 bg-[#111111] p-8" data-testid="athlete-socials">
              <h2 className="font-display text-3xl mb-4">Connect</h2>
              <div className="flex flex-wrap gap-3">
                {Object.entries(athlete.socials).map(([key, val]) => {
                  if (!val) return null;
                  const Icon = SOCIAL_ICONS[key] || Globe;
                  return (
                    <a
                      key={key}
                      href={val}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`social-${key}`}
                      className="border border-white/20 hover:border-[#FF5A00] hover:text-[#FF5A00] transition-colors px-4 py-3 flex items-center gap-2 text-xs uppercase tracking-[0.15em]"
                    >
                      <Icon size={14} /> {key}
                    </a>
                  );
                })}
              </div>
            </div>
          )}

          {athlete.gallery?.length > 0 && (
            <div className="border border-white/10 bg-[#111111] p-8" data-testid="athlete-gallery">
              <h2 className="font-display text-3xl mb-4">Media Kit</h2>
              <p className="label-mono mb-4">Stamped with your chosen logo placement</p>
              <div className="grid grid-cols-2 gap-4">
                {athlete.gallery.map((g, i) => (
                  <div key={i} className="relative group">
                    <img src={absoluteUrl(g)} alt="" className="w-full aspect-square object-cover" />
                    <a
                      href={downloadUrl(g, `${athlete.slug}-photo-${i + 1}`, { position: MARK })}
                      data-testid={`download-gallery-${i}`}
                      className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-[#FF5A00]"
                    >
                      <Download size={22} />
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
